import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAccountVendorBills } from "hooks/useAccountVendorBill"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useAccounts } from "hooks/useAccountEntity"
import {
  useCreateAccountVendorPayment,
} from "hooks/useAccountVendorPayment"

const AccountVendorPaymentAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountVendorPayment()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: billResponse, isLoading: billLoading } = useAccountVendorBills(
    { all: true, per_page: 200 },
    modal
  )
  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, modal)
  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)

  const bills = billResponse?.data?.data || billResponse?.data || []
  const paymentMethods = paymentMethodResponse?.data?.data || paymentMethodResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []

  const billOptions = useMemo(
    () =>
      Array.isArray(bills)
        ? bills.map(item => ({
            value: item.id,
            label: `${item.bill_no} - ${item.vendor?.name || "Vendor"}`,
          }))
        : [],
    [bills]
  )

  const paymentMethodOptions = useMemo(
    () =>
      Array.isArray(paymentMethods)
        ? paymentMethods.map(item => ({ value: item.id, label: item.name }))
        : [],
    [paymentMethods]
  )

  const accountOptions = useMemo(
    () => (Array.isArray(accounts) ? accounts.map(item => ({ value: item.id, label: item.name })) : []),
    [accounts]
  )

  const validationSchema = Yup.object({
    vendor_bill_id: Yup.mixed().required("Please select a vendor bill"),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter amount"),
    payment_method_id: Yup.mixed().required("Please select a payment method"),
    account_id: Yup.mixed().required("Please select an account"),
    payment_date: Yup.string().required("Please select payment date"),
    reference_number: Yup.string().nullable().max(255, "Reference number cannot exceed 255 characters"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        reference_number: values.reference_number?.trim() || null,
        notes: values.notes?.trim() || null,
        image_ids: voucherImages.map(img => img.id),
      }

      try {
        const res = await createMutation(payload)
        toast.success(res?.message ?? "Vendor payment created successfully.")
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save vendor payment."
        toast.error(msg)
      }
    },
    [createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vendor_bill_id: editedData?.vendor_bill_id || editedData?.vendorBill?.id || "",
      amount: editedData?.amount ?? "",
      payment_method_id: editedData?.payment_method_id || editedData?.paymentMethod?.id || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      payment_date: editedData?.payment_date || "",
      reference_number: editedData?.reference_number || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || billLoading || paymentMethodLoading || accountLoading

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title={isEdit ? "View Vendor Payment" : "Add Vendor Payment"} size="xl">
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Payment Information</h5>
            <Row>
              <Col md={6}><FormikCreatableSelect name="vendor_bill_id" formik={formik} label="Vendor Bill" options={billOptions} loading={billLoading} required /></Col>
              <Col md={6}><div className="mb-3"><Label htmlFor="amount">Amount</Label><InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />{formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}</div></Col>
            </Row>
            <Row>
              <Col md={6}><FormikCreatableSelect name="payment_method_id" formik={formik} label="Payment Method" options={paymentMethodOptions} loading={paymentMethodLoading} required /></Col>
              <Col md={6}><FormikCreatableSelect name="account_id" formik={formik} label="Account" options={accountOptions} loading={accountLoading} required /></Col>
            </Row>
            <Row>
              <Col md={6}><div className="mb-3"><Label htmlFor="payment_date">Payment Date</Label><InputField type="date" name="payment_date" id="payment_date" value={formik.values.payment_date} onChange={formik.handleChange} invalid={formik.touched.payment_date && !!formik.errors.payment_date} />{formik.touched.payment_date && formik.errors.payment_date && <FormFeedback type="invalid">{formik.errors.payment_date}</FormFeedback>}</div></Col>
              <Col md={6}><div className="mb-3"><Label htmlFor="reference_number">Reference Number</Label><InputField type="text" name="reference_number" id="reference_number" value={formik.values.reference_number} onChange={formik.handleChange} invalid={formik.touched.reference_number && !!formik.errors.reference_number} />{formik.touched.reference_number && formik.errors.reference_number && <FormFeedback type="invalid">{formik.errors.reference_number}</FormFeedback>}</div></Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
              {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
            </div>
            <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} />
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : "Add Vendor Payment"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountVendorPaymentAddEdit
